import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IReportState, IApplicationState, AppActionCreators, ReportActionCreators, INcsScoresReportResponse, IHardToPlaceDonorsResponse, IAppState } from '@nkr/store/index';
import { IHardToPlaceDonor, INcsScoresReport, INcsScoresReportTotals } from '@nkr/models/index';
import { Button, ButtonGroup } from 'react-bootstrap';
import { GridApi, ProcessCellForExportParams, ExcelExportParams, GridReadyEvent, ColumnApi, RowHeightCallbackParams, ColumnWidthCallbackParams } from 'ag-grid-community';
import { LegendWrapper } from './styles';
import SummaryGrid from './summary-grid';
import Tabs from '@nkr/views/swap-management/tabs-new';
import DetailGrid from './detail-grid';
import { CompositeScoreOverlay } from '@nkr/views/center-admin/action-center/center-performance/relative-performance-overlays';
import AuditReportScoreTable from '../audit-report-score-table';
import HardToPlaceDonors from './hard-to-place-donors';

interface IOwnProps {
    isAdmin: boolean
}

interface IStateProps {
    app: IAppState,
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getNcsScoresReport: () => Promise<INcsScoresReportResponse>,
    getHardToPlaceDonors: (isAdmin: boolean, centerId: number) => Promise<IHardToPlaceDonorsResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: INcsScoresReport[],
    show: boolean,
    centerType: number,
    gridApi: GridApi,
    columnApi: ColumnApi,
    showHardToPlaceDonors: boolean,
    hardToPlaceCenter: string,
    hardToPlaceDonorData: IHardToPlaceDonor[]
}

class CenterNCSScoresReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        show: false,
        centerType: 2,
        gridApi: null,
        columnApi: null,
        showHardToPlaceDonors: false,
        hardToPlaceCenter: '',
        hardToPlaceDonorData: []
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api,
            columnApi: e.columnApi
        });

        e.api.sizeColumnsToFit();

        let cs = e.columnApi.getColumnState();

        let colId = this.props.isAdmin ? 'rank' : 'compScore';
        let sort = this.props.isAdmin ? 'asc' : 'desc';

        let s = cs.find(x => x.colId == colId);
        s.sort = sort;

        e.columnApi.applyColumnState({ state: cs });
    };

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getNcsScoresReport()
                .then(response => {
                    this.setState({
                        data: response.data.results
                    });

                    this.props.hideLoading();
                })
                .catch((ex) => {
                    alert('An error occured while loading report data');
                    this.props.hideLoading();
                });
        });
    }

    getFilteredData = () => {
        let { centerType, data } = this.state;

        switch (centerType) {
            case 1: // Partner Centers
                return data.filter(item => item.isAdpPartner == true);

            case 2: // All Member Centers
                return data.filter(item => item.isNkrParticipant == true);

            case 3: // All Centers
                return data;

            default:
                return []; // or null if you want to indicate "no data"
        }
    };

    getTotals = (data: INcsScoresReport[]): INcsScoresReportTotals[] => {
        let totalclCu = _.sumBy(data, item => item.clCu);
        let totalOldChains12 = _.sumBy(data, item => item.oldChains12);
        let totalchains12 = _.sumBy(data, item => item.chains12);
        let totalchains24 = _.sumBy(data, item => item.chains24);
        let totaldeclines12 = _.sumBy(data, item => item.declines12);
        let totaloChains24 = _.sumBy(data, item => item.oChains24);
        let totalofvChains24 = _.sumBy(data, item => item.ofvChains24);
        let totalnonofvChains24 = _.sumBy(data, item => item.nonofvChains24);
        let totalfvCume = _.sumBy(data, item => item.fvCume);
        let totalWlpOffers = _.sumBy(data, item => item.wlpOffers);
        let totalWlpDeclines = _.sumBy(data, item => item.wlpDeclines);
        let totalDeclineRatio = _.round(totalWlpOffers > 0 ? (totalWlpDeclines / totalWlpOffers) * 100 : 0, 1);
        let totalOldDeclineRatio12 = _.round(totalOldChains12 > 0 ? (totaldeclines12 / totalOldChains12) * 100 : 0, 1);
        let totalDeclineRatio12 = _.round(totalchains12 > 0 ? (totaldeclines12 / totalchains12) * 100 : 0, 1);
        let totaloChainsRatio24 = _.round(totalchains24 > 0 ? (totaloChains24 / totalchains24) * 100 : 0, 1);
        let totalofvChainsRatio24 = _.round((totalofvChains24 + totalnonofvChains24) > 0 ? (totalofvChains24 / (totalnonofvChains24 + totalofvChains24)) * 100 : 0, 1);
        let totaladpStarts = _.sumBy(data, item => item.adpStarts);
        let totalnddStarts = _.sumBy(data, item => item.nddStarts);
        let totalfcstADPStarts = _.sumBy(data, item => item.fcstADPStarts);
        let totalfcstNDDStarts = _.sumBy(data, item => item.fcstNDDStarts);
        let totaladpEnds = _.sumBy(data, item => item.adpEnds);
        let totalnddEnds = _.sumBy(data, item => item.nddEnds);
        let totalfcstADPEnds = _.sumBy(data, item => item.fcstADPEnds);
        let totalfcstNDDEnds = _.sumBy(data, item => item.fcstNDDEnds);
        let totaltotalStarts = _.sumBy(data, item => item.totalStarts);
        let totaltotalEnds = _.sumBy(data, item => item.totalEnds);
        let totalnddncSa = _.sumBy(data, item => item.nddncSa);
        let totaladpncSa = _.sumBy(data, item => item.adpncSa);
        let totalsummaryNcSa = _.sumBy(data, item => item.summaryNCSa);
        let totaldetailsNcSa = _.sumBy(data, item => item.detailsNCSa);
        let totalncSb = _.sumBy(data, item => item.ncSb);
        let totalncSu = _.sumBy(data, item => item.ncSu);
        let totalncsDeficit = _.sumBy(data, item => item.ncsDeficit);
        let totalfcstTotalStarts = _.sumBy(data, item => item.fcstTotalStarts);
        let totalfcstTotalEnds = _.sumBy(data, item => item.fcstTotalEnds);
        let totalfcstNDDNCS = _.sumBy(data, item => item.fcstNDDNCS);
        let totalfcstADPNCS = _.sumBy(data, item => item.fcstADPNCS);
        let totalfcstNCS = _.sumBy(data, item => item.fcstNCS);
        let totalofvTargets = _.sumBy(data, item => item.ofvTarget);
        let totalnfvTargets = _.sumBy(data, item => item.nfvTarget);
        let totalcsfvStartersLast24 = _.sumBy(data, item => item.csfvStartersLast24);
        let totalcssvStartersLast24 = _.sumBy(data, item => item.cssvStartersLast24);
        let totalcsCompPairsLast24 = _.sumBy(data, item => item.csCompPairsLast24);
        let totalcsRemoteSVOrPDLast24 = _.sumBy(data, item => item.csRemoteSVOrPDLast24);
        let totalCompScore = _.sumBy(data, item => item.compScore);
        let totalShareOfCompScore = _.sumBy(data, item => item.shareOfCompScore);
        let totalShareOfFloat = _.sumBy(data, item => item.shareOfFloat);
        let totalTargetChainEnds = _.sumBy(data, item => item.targetChainEnds);
        let totalPartnerCenter = data.filter(x => x.isAdpPartner == true).length;
        let totalMemberCenter = data.filter(x => x.isNkrParticipant == true).length;
        let totalAnyAnatomy = data.filter(x => x.anyAnatomy == true).length;
        let totalAnyAnatomyZeroDeclineBonus = _.sumBy(data, item => item.anyAnatomyDeclineRatioBonus);
        let donorAcceptanceBonus = _.sumBy(data, item => item.donorAcceptanceBonus);

        return [{
            centerId: 0,
            centerAlias: '',
            isAdpPartner: null,
            isNkrParticipant: null,
            centerState: '',
            clCu: totalclCu,
            abWlp: '',
            wlpOffers: totalWlpOffers,
            wlpDeclines: totalWlpDeclines,
            declineRatio: totalDeclineRatio,
            oldChains12: totalOldChains12,
            chains12: totalchains12,
            chains24: totalchains24,
            declines12: totaldeclines12,
            ofvChains24: totalofvChains24,
            nonofvChains24: totalnonofvChains24,
            fvCume: totalfvCume,
            oChainsRatio24: totaloChainsRatio24,
            oldDeclineRatio12: totalOldDeclineRatio12,
            declineRatio12: totalDeclineRatio12,
            ofvChainsRatio24: totalofvChainsRatio24,
            adpStarts: totaladpStarts,
            nddStarts: totalnddStarts,
            fcstADPStarts: totalfcstADPStarts,
            fcstNDDStarts: totalfcstNDDStarts,
            adpEnds: totaladpEnds,
            nddEnds: totalnddEnds,
            fcstADPEnds: totalfcstADPEnds,
            fcstNDDEnds: totalfcstNDDEnds,
            totalStarts: totaltotalStarts,
            totalEnds: totaltotalEnds,
            nddncSa: totalnddncSa,
            adpncSa: totaladpncSa,
            summaryNCSa: totalsummaryNcSa,
            detailsNCSa: totaldetailsNcSa,
            fcstTotalStarts: totalfcstTotalStarts,
            fcstTotalEnds: totalfcstTotalEnds,
            fcstNDDNCS: totalfcstNDDNCS,
            fcstADPNCS: totalfcstADPNCS,
            fcstNCS: totalfcstNCS,
            oChains24: totaloChains24,
            ncSb: totalncSb,
            ncsDeficit: totalncsDeficit,
            ncSu: totalncSu,
            ofvTarget: totalofvTargets,
            nfvTarget: totalnfvTargets,
            anyAnatomy: null,
            anatomyRestrictions: '',
            csfvStartersLast24: totalcsfvStartersLast24,
            cssvStartersLast24: totalcssvStartersLast24,
            csCompPairsLast24: totalcsCompPairsLast24,
            csRemoteSVOrPDLast24: totalcsRemoteSVOrPDLast24,
            anyAnatomyDeclineRatioBonus: totalAnyAnatomyZeroDeclineBonus,
            compScore: totalCompScore,
            shareOfCompScore: Math.round(totalShareOfCompScore),
            shareOfFloat: totalShareOfFloat,
            targetChainEnds: totalTargetChainEnds,
            partnerCenterCount: totalPartnerCenter,
            memberCenterCount: totalMemberCenter,
            anyAnatomyCount: totalAnyAnatomy,
            rank: 0,
            donorAcceptanceBonus
        }];
    }

    handleFilteredData = (centerType: number) => {
        this.setState({
            centerType: centerType,
            show: false
        });
    };

    handleDefaultSort = () => {
        let cs = this.state.columnApi.getColumnState();

        cs.forEach(col => {
            if (col.colId != 'colorRank') {
                col.sort = null;
            } else {
                col.sort = 'asc';
            }
        });

        this.state.columnApi.applyColumnState({ state: cs });
    }

    handleExport = () => {
        let fileName = !this.state.show ? 'NcsSummaryReport.xlsx' : 'NcsDetailReport.xlsx';

        let params: ExcelExportParams = {
            columnGroups: true,
            fileName: fileName,
            rowHeight: (params: RowHeightCallbackParams) => {
                if (params.rowIndex == 1) {
                    return 80;
                }

                return 25;
            },
            columnWidth: (params: ColumnWidthCallbackParams) => {
                let colDef = params.column.getColDef();

                if (colDef.colId == 'isAdpPartner' || colDef.colId == 'compScore' || colDef.colId == 'shareOfCompScore') {
                    return 85;
                }

                return colDef.width;
            },
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (this.state.show == false) {
                    if (colDef.colId == 'isAdpPartner') {
                        if (params.node.data.centerId > 0) {
                            return params.value ? 'YES' : 'NO';
                        } else {
                            return params.node.data.partnerCenterCount;
                        }
                    }

                    if (colDef.colId == 'anyAnatomy') {
                        if (params.node.data.centerId > 0) {
                            return params.value ? 'YES' : 'NO';
                        } else {
                            return params.node.data.anyAnatomyCount;
                        }
                    }
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsExcel(params);
    }

    handleShowHardToPlace = (centerId: number) => {
        this.props.showLoading('Grabbing Hard to Place Donor Records').then(() => {
            this.props.getHardToPlaceDonors(this.props.isAdmin, centerId)
                .then(response => {
                    if (response.data.success) {
                        let { data } = this.state;

                        let hardToPlaceDonorData = response.data.results;

                        this.setState({
                            hardToPlaceCenter: data.filter(x => x.centerId == centerId)[0]?.centerAlias,
                            hardToPlaceDonorData,
                            showHardToPlaceDonors: true
                        })
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        })
    }

    handleCloseHardToPlaceDonors = () => {
        this.setState({
            hardToPlaceDonorData: [],
            showHardToPlaceDonors: false,
            hardToPlaceCenter: ''
        });
    };

    render = () => {
        const filteredData = this.getFilteredData();
        const totals = this.getTotals(filteredData);

        return (
            <>
                {this.props.isAdmin ?
                    <>
                        <Tabs tabKey='NCS' subTabKey='' counts={null} user={this.props.app.user} />

                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginBottom: 5 }}>
                            <ButtonGroup bsSize='sm'>
                                <Button
                                    bsStyle={this.state.centerType == 1 ? 'primary' : 'default'}
                                    onClick={() => this.handleFilteredData(1)}
                                >
                                    View Partner Centers
                                </Button>

                                <Button
                                    bsStyle={this.state.centerType == 2 ? 'primary' : 'default'}
                                    onClick={() => this.handleFilteredData(2)}
                                >
                                    View All Member Centers
                                </Button>

                                <Button
                                    bsStyle={this.state.centerType == 3 ? 'primary' : 'default'}
                                    onClick={() => this.handleFilteredData(3)}
                                >
                                    View All Centers
                                </Button>
                            </ButtonGroup>

                            <LegendWrapper>
                                <div className='legend-item'>
                                    <div className='legend-item-color' style={{ backgroundColor: '#79ae76' }}></div>
                                    <div className='legend-item-text'>- NCSu {'>'} 0, Is Not PC</div>
                                </div>

                                <div className='legend-item'>
                                    <div className='legend-item-color' style={{ backgroundColor: '#90c5d5' }}></div>
                                    <div className='legend-item-text'>- TCE {'>='} 0, WLDR {'<'} 30%, Is PC</div>
                                </div>

                                <div className='legend-item'>
                                    <div className='legend-item-color' style={{ backgroundColor: '#6883ca' }}></div>
                                    <div className='legend-item-text'>- TCE {'<'} 0, Is PC</div>
                                </div>

                                <div className='legend-item'>
                                    <div className='legend-item-color' style={{ backgroundColor: '#cdcccb' }}></div>
                                    <div className='legend-item-text'>- WLDR {'>='} 30%, Is PC</div>
                                </div>

                                <div className='legend-item'>
                                    <div className='legend-item-color' style={{ backgroundColor: '#d27a79' }}></div>
                                    <div className='legend-item-text'>- NCSu {'<'} 0, Is Not PC</div>
                                </div>

                                <div className='legend-item'>
                                    <div className='legend-item-color' style={{ backgroundColor: 'white', border: '1px solid black' }}></div>
                                    <div className='legend-item-text'>- NCSu = 0, Is Not PC</div>
                                </div>

                                {this.state.centerType == 3 && (
                                    <div className='legend-item'>
                                        <div className='legend-item-color' style={{ backgroundColor: 'orange' }}></div>
                                        <div className='legend-item-text'>- Is Not MC</div>
                                    </div>
                                )}
                            </LegendWrapper>

                            <div style={{ marginLeft: 'auto' }}>
                                <Button bsStyle='success' onClick={this.handleExport}>Export CSV</Button>
                            </div>
                        </div>
                    </>
                    :
                    null
                }

                {!this.state.show ?
                    <>
                        {this.props.app.center != null && filteredData.filter((c) => c.centerId == this.props.app.center.id).map((d) =>
                            <div className='well well-sm' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <AuditReportScoreTable
                                    reportName='Composite Score'
                                    scoreValue={d.compScore.toString()}
                                    overlay={<CompositeScoreOverlay />}
                                />
                            </div>
                        )}

                        <SummaryGrid
                            data={filteredData}
                            totals={totals}
                            isAdmin={this.props.isAdmin}
                            centerId={this.props.app.center != null ? this.props.app.center.id : 0}
                            centerFilterType={this.state.centerType}
                            onGridReady={this.onGridReady}
                            onClickHardToPlace={this.handleShowHardToPlace}
                        />
                    </>
                    :
                    <DetailGrid
                        data={filteredData}
                        totals={totals}
                        onGridReady={this.onGridReady} />
                }

                <HardToPlaceDonors
                    isAdmin={this.props.app.user.group.isSuper}
                    show={this.state.showHardToPlaceDonors}
                    center={this.state.hardToPlaceCenter}
                    data={this.state.hardToPlaceDonorData}
                    onHideModal={this.handleCloseHardToPlaceDonors}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getNcsScoresReport: () => {
            return dispatch(ReportActionCreators.getNcsScoresReport());
        },
        getHardToPlaceDonors: (isAdmin, centerId) => {
            return dispatch(ReportActionCreators.getHardToPlaceDonors(isAdmin, centerId))
        }
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterNCSScoresReport);